import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useStores } from "../lib/stores";
import { GST_STATES } from "../constants/gstStates";
import { DEFAULT_STORE_THEME, STORE_THEMES, applyStoreTheme } from "../lib/storeThemes";
import ImageUploader from "../components/ImageUploader";
import { ArrowLeft, Building2 } from "lucide-react";
import { toast } from "sonner";

const emptyBilling = {
  billing_address: "",
  billing_city: "",
  billing_state: "",
  billing_country: "India",
  billing_pincode: "",
};

const empty = {
  name: "",
  image: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  state: "",
  country: "India",
  pincode: "",
  gst_number: "",
  product_price_includes_gst: true,
  allow_zero_stock_bill: false,
  theme: DEFAULT_STORE_THEME,
  billing_same_as_office: true,
  ...emptyBilling,
};

function toForm(store, loginEmail) {
  const same = store.billing_same_as_office !== false && store.billing_same_as_office !== 0;
  return {
    name: store.name || "",
    image: store.image || "",
    phone: store.phone || "",
    email: store.email || loginEmail || "",
    address: store.address || "",
    city: store.city || "",
    state: store.state || "",
    country: store.country || "India",
    pincode: store.pincode || "",
    gst_number: store.gst_number || "",
    product_price_includes_gst: store.product_price_includes_gst !== false,
    allow_zero_stock_bill: store.allow_zero_stock_bill === true,
    theme: store.theme || DEFAULT_STORE_THEME,
    billing_same_as_office: same,
    billing_address: store.billing_address || "",
    billing_city: store.billing_city || "",
    billing_state: store.billing_state || "",
    billing_country: store.billing_country || "India",
    billing_pincode: store.billing_pincode || "",
  };
}

export default function StoreProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { reload: reloadStores } = useStores();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/stores/${id}`)
      .then((r) => setForm(toForm(r.data, user?.email)))
      .catch(() => {
        toast.error("Store not found");
        navigate("/app/stores");
      })
      .finally(() => setLoading(false));
  }, [id, user?.email, navigate]);

  const setBillingSame = (checked) => {
    setForm((f) => ({
      ...f,
      billing_same_as_office: checked,
      ...(checked ? emptyBilling : {}),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Store name is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        image: form.image || null,
        gst_number: form.gst_number?.trim() || null,
        product_price_includes_gst: form.product_price_includes_gst,
        allow_zero_stock_bill: form.allow_zero_stock_bill,
        billing_same_as_office: form.billing_same_as_office,
      };
      if (form.billing_same_as_office) {
        Object.assign(payload, emptyBilling);
      }
      await api.put(`/stores/${id}`, payload);
      toast.success("Store profile updated");
      await reloadStores();
      applyStoreTheme(form.theme);
      navigate("/app/stores");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  const storeImages = form.image ? [form.image] : [];

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="store-profile-root">
      <Link
        to="/app/stores"
        className="inline-flex items-center gap-2 text-sm font-medium mb-4"
        style={{ color: "var(--text-muted)" }}
        data-testid="store-profile-back"
      >
        <ArrowLeft size={16} /> Back to stores
      </Link>

      <div className="mb-6">
        <h1 className="font-display font-bold text-3xl">Store profile</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
          Update your store details and billing address in one simple form.
        </p>
      </div>

      <form onSubmit={save} className="card-soft p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Store name*" testid="store-profile-name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Phone" testid="store-profile-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <Field label="Store email" testid="store-profile-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} hint="For invoices only — does not change login email" />
          <Field label="GSTIN (optional)" testid="store-profile-gst" value={form.gst_number} onChange={(v) => setForm({ ...form, gst_number: v })} hint="15-char GST number" />
          <div className="sm:col-span-2">
            <label className="flex items-center gap-3 text-sm font-medium cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="store-profile-includes-gst"
                checked={form.product_price_includes_gst}
                onChange={(e) => setForm({ ...form, product_price_includes_gst: e.target.checked })}
                className="w-4 h-4 rounded border border-[var(--border-soft)]"
              />
              Price Inlusive GST
            </label>
            <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
              Select this if listed product prices already contain GST.
            </p>
          </div>
          <div className="sm:col-span-2">
            <label className="flex items-center gap-3 text-sm font-medium cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="store-profile-allow-zero-stock"
                checked={form.allow_zero_stock_bill}
                onChange={(e) => setForm({ ...form, allow_zero_stock_bill: e.target.checked })}
                className="w-4 h-4 rounded border border-[var(--border-soft)]"
              />
              Allow 0 or less stock to bill
            </label>
            <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
              When enabled, POS can bill items even when stock is zero or negative.
            </p>
          </div>
        </div>

        <ImageUploader
          images={storeImages}
          onChange={(imgs) => setForm({ ...form, image: imgs[0] || "" })}
          max={1}
          label="Store image / logo"
          hint="Drag & drop or click — JPG, PNG, WebP or GIF · 1 MB max · resized to 500px"
        />

        <AddressSection
          title="Office address"
          prefix="office"
          form={form}
          onChange={(patch) => setForm({ ...form, ...patch })}
          addressTestid="store-profile-address"
          cityTestid="store-profile-city"
          stateTestid="store-profile-state"
          countryTestid="store-profile-country"
          pincodeTestid="store-profile-pincode"
        />

        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="store-profile-billing-same"
            checked={form.billing_same_as_office}
            onChange={(e) => setBillingSame(e.target.checked)}
            className="w-4 h-4 rounded border border-[var(--border-soft)]"
          />
          Billing address same as office
        </label>

        {!form.billing_same_as_office && (
          <AddressSection
            title="Billing address"
            prefix="billing"
            form={form}
            onChange={(patch) => setForm({ ...form, ...patch })}
            addressTestid="store-profile-billing-address"
            cityTestid="store-profile-billing-city"
            stateTestid="store-profile-billing-state"
            countryTestid="store-profile-billing-country"
            pincodeTestid="store-profile-billing-pincode"
          />
        )}

        <div className="card-soft p-6">
          <h2 className="font-display font-semibold text-base">Store theme</h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            Choose a theme for this store. Preview is applied immediately.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {STORE_THEMES.map((t) => {
              const selected = form.theme === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`store-theme-${t.id}`}
                  onClick={() => {
                    setForm({ ...form, theme: t.id });
                    applyStoreTheme(t.id);
                  }}
                  className="flex flex-col items-start gap-3 rounded-2xl border px-4 py-4 text-left"
                  style={{
                    borderColor: selected ? "var(--primary)" : "var(--border-soft)",
                    background: selected ? "rgba(139, 30, 63, 0.05)" : "white",
                  }}
                >
                  <div className="flex h-12 w-full overflow-hidden rounded-xl border" style={{ borderColor: "var(--border-soft)" }}>
                    <span className="flex-1" style={{ background: t.primary }} />
                    <span className="flex-1" style={{ background: t.accent }} />
                    <span className="flex-1" style={{ background: t.background }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{t.name}</div>
                    <div className="text-xs text-[var(--text-muted)]">{t.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <Link
            to="/app/stores"
            className="inline-flex items-center justify-center rounded-full border border-[var(--border-soft)] px-5 py-3 text-sm font-medium"
            style={{ color: "var(--text-main)" }}
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            data-testid="store-profile-save"
            className="inline-flex items-center justify-center rounded-full bg-[var(--primary)] px-5 py-3 text-sm font-semibold text-white tactile-btn disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save profile"}
          </button>
        </div>
      </form>
    </div>
  );
}

function AddressSection({ title, prefix, form, onChange, addressTestid, cityTestid, stateTestid, countryTestid, pincodeTestid }) {
  const addressKey = prefix === "billing" ? "billing_address" : "address";
  const cityKey = prefix === "billing" ? "billing_city" : "city";
  const stateKey = prefix === "billing" ? "billing_state" : "state";
  const countryKey = prefix === "billing" ? "billing_country" : "country";
  const pincodeKey = prefix === "billing" ? "billing_pincode" : "pincode";

  return (
    <div className="card-soft p-6 space-y-4">
      <div>
        <h2 className="font-display font-semibold text-base">{title}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="sm:col-span-2 lg:col-span-4">
          <label className="text-sm font-medium">Street address</label>
          <textarea
            data-testid={addressTestid}
            value={form[addressKey]}
            onChange={(e) => onChange({ [addressKey]: e.target.value })}
            rows={2}
            className="mt-1 w-full px-3 py-3 rounded-2xl border bg-white"
            style={{ borderColor: "var(--border-soft)" }}
          />
        </div>
        <Field label="City" testid={cityTestid} value={form[cityKey]} onChange={(v) => onChange({ [cityKey]: v })} />
        <div>
          <label className="text-sm font-medium">State (GST)</label>
          <select
            data-testid={stateTestid}
            value={form[stateKey]}
            onChange={(e) => onChange({ [stateKey]: e.target.value })}
            className="mt-1 w-full h-11 px-3 rounded-2xl border bg-white"
            style={{ borderColor: "var(--border-soft)" }}
          >
            <option value="">Select state</option>
            {GST_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <Field label="Country" testid={countryTestid} value={form[countryKey]} onChange={(v) => onChange({ [countryKey]: v })} />
        <Field label="Pincode" testid={pincodeTestid} value={form[pincodeKey]} onChange={(v) => onChange({ [pincodeKey]: v })} />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, hint }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      <input
        data-testid={testid}
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-11 px-3 rounded-2xl border bg-white"
        style={{ borderColor: "var(--border-soft)" }}
      />
      {hint && <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{hint}</p>}
    </div>
  );
}
